'use client';

import React, { useState } from 'react';
import type { Source } from '@/lib/types';
import { cn } from '@/lib/utils';
import { formatAllBibTeX, formatBibTeX, formatAPA } from '@/lib/bibtex';

/**
 * CitationsRail — sticky right rail (280px) on the article page.
 *
 * Spec: design-references/article.html `.a-rail`, `.a-cite`.
 *   - Header: "N sources" + "Copy all .bib" button
 *   - Per-citation card: number, title, authors/journal/year, PMID link,
 *     + inline actions (Copy BibTeX / Copy APA / View on PubMed)
 *   - Cite refs in the article body link to `#c{n}` and flash the card
 *     on arrival — the flash is driven by `:target` state in CSS plus a
 *     transient class for the smooth-scroll trigger.
 *
 * The CSS for `.a-cite.is-flash` lives in globals.css (port + a-rail helpers).
 */

export interface CitationsRailProps {
  sources: Source[];
  className?: string;
}

export function CitationsRail({ sources, className }: CitationsRailProps) {
  const [copied, setCopied] = useState<string | null>(null);

  const flash = (key: string) => {
    setCopied(key);
    setTimeout(() => setCopied((k) => (k === key ? null : k)), 1400);
  };

  const copyAll = async () => {
    const text = formatAllBibTeX(sources);
    try {
      await navigator.clipboard.writeText(text);
      flash('__all');
    } catch {}
  };

  const copyOne = async (source: Source, kind: 'bib' | 'apa') => {
    const text = kind === 'bib' ? formatBibTeX(source) : formatAPA(source);
    try {
      await navigator.clipboard.writeText(text);
      flash(`${kind}-${source.id}`);
    } catch {}
  };

  return (
    <aside
      className={cn('sticky top-20 max-h-[calc(100vh-100px)] overflow-y-auto pr-1', className)}
      aria-label={`${sources.length} citations`}
    >
      <div
        className={cn(
          'flex justify-between items-center',
          'font-mono text-[10px] font-medium tracking-[0.14em] uppercase text-slate',
          'pb-2.5 mb-3.5 border-b border-border'
        )}
      >
        <span>{sources.length} sources</span>
        <button
          type="button"
          onClick={copyAll}
          className={cn(
            'px-2 py-1 rounded-xs bg-white border border-border',
            'font-ui text-[10.5px] font-medium normal-case tracking-[0.02em] cursor-pointer',
            'transition-colors duration-fast',
            copied === '__all'
              ? 'text-tier-strong border-tier-strong'
              : 'text-aviado-blue hover:border-aviado-blue'
          )}
        >
          {copied === '__all' ? '✓ Copied' : 'Copy all .bib'}
        </button>
      </div>

      <ol className="list-none p-0 m-0">
        {sources.map((source, i) => {
          const n = i + 1;
          const pubmedUrl = source.pmid
            ? `https://pubmed.ncbi.nlm.nih.gov/${source.pmid}/`
            : null;
          return (
            <li
              key={source.id ?? String(n)}
              id={`c${n}`}
              className={cn(
                'bg-white border border-border rounded-[10px]',
                'p-[14px_16px] mb-2.5',
                'scroll-mt-[100px]',
                'transition-[border-color,box-shadow] duration-fast',
                'target:border-aviado-teal target:shadow-[0_0_0_3px_color-mix(in_oklab,_var(--aviado-teal)_20%,_transparent)]'
              )}
            >
              <div className="font-mono text-[10px] font-medium tracking-[0.1em] text-aviado-teal-text mb-1">
                [{n}]
              </div>
              <h5
                className="font-heading font-semibold text-[13px] leading-[1.35] text-ink m-0 mb-1.5"
                style={{ fontVariationSettings: '"opsz" 16' }}
              >
                {source.title}
              </h5>
              <div className="font-mono text-[10.5px] leading-[1.5] tracking-[0.02em] text-slate">
                {[source.authors, source.publication, source.year]
                  .filter(Boolean)
                  .join('. ')}
                {source.pmid && (
                  <>
                    <br />
                    PMID <span className="text-aviado-blue">{source.pmid}</span>
                  </>
                )}
              </div>

              <div className="flex gap-3 mt-2 pt-2 border-t border-border font-mono text-[10px] tracking-[0.04em] text-slate">
                <RailActionButton
                  onClick={() => copyOne(source, 'bib')}
                  flashed={copied === `bib-${source.id}`}
                >
                  {copied === `bib-${source.id}` ? '✓ Copied' : 'Copy BibTeX'}
                </RailActionButton>
                <RailActionButton
                  onClick={() => copyOne(source, 'apa')}
                  flashed={copied === `apa-${source.id}`}
                >
                  {copied === `apa-${source.id}` ? '✓ Copied' : 'Copy APA'}
                </RailActionButton>
                {pubmedUrl && (
                  <a
                    href={pubmedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-transparent border-0 p-0 text-aviado-blue no-underline hover:underline font-mono text-[10px] tracking-[0.04em]"
                  >
                    View on PubMed
                  </a>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </aside>
  );
}

function RailActionButton({
  children,
  flashed,
  onClick,
}: {
  children: React.ReactNode;
  flashed: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'bg-transparent border-0 p-0 cursor-pointer font-mono text-[10px] tracking-[0.04em]',
        'hover:underline transition-colors duration-fast',
        flashed ? 'text-tier-strong' : 'text-aviado-blue'
      )}
    >
      {children}
    </button>
  );
}

export default CitationsRail;
